import { Breadcrumb, Grid } from 'antd';
import { Link, useMatches } from 'react-router';
import type { ReactNode } from 'react';

export interface Crumb {
  /** Label shown on the breadcrumb. */
  label: ReactNode;
  /** Optional href; omit for the current/last crumb. */
  to?: string;
}

interface Props {
  /**
   * Explicit crumb list. When omitted, derives from React Router's `useMatches`
   * by reading `handle.crumb` on each matched route — either a string or a
   * function `(params, data) => string`.
   */
  items?: Crumb[];
}

type RouteHandle = {
  crumb?: string | ((params: Record<string, string | undefined>, data: unknown) => string);
};

const { useBreakpoint } = Grid;

export function Breadcrumbs({ items }: Props) {
  const matches = useMatches();
  const screens = useBreakpoint();

  const resolved: Crumb[] =
    items ??
    matches
      .filter((m) => (m.handle as RouteHandle | undefined)?.crumb != null)
      .map((m, idx, arr) => {
        const handle = m.handle as RouteHandle;
        const crumbValue = handle.crumb;
        const label =
          typeof crumbValue === 'function'
            ? crumbValue(m.params as Record<string, string | undefined>, m.data)
            : (crumbValue as string);
        return {
          label,
          to: idx < arr.length - 1 ? m.pathname : undefined,
        };
      });

  if (resolved.length === 0) return null;

  // Mobile: collapse to "… › current" if more than 2 crumbs.
  const visible =
    !screens.md && resolved.length > 2
      ? [{ label: '…', to: undefined }, resolved[resolved.length - 1]]
      : resolved;

  return (
    <Breadcrumb
      items={visible.map((c) => ({
        title: c.to ? <Link to={c.to}>{c.label}</Link> : c.label,
      }))}
    />
  );
}
